/**
 * WebSocket Hook for Real-time Notifications
 * 
 * Connects to backend WebSocket for live updates:
 * - Notifications
 * - Item updates
 * - Comments
 * - Typing indicators
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  relatedItemId?: string;
  relatedUserId?: string;
  actionUrl?: string;
  createdAt: string;
  isRead: boolean;
}

interface UseWebSocketOptions {
  autoConnect?: boolean;
  reconnection?: boolean;
  reconnectionAttempts?: number;
  reconnectionDelay?: number;
}

interface UseWebSocketReturn {
  socket: Socket | null;
  connected: boolean;
  connecting: boolean;
  error: Error | null;
  notifications: Notification[];
  unreadCount: number;
  connect: () => void;
  disconnect: () => void;
  joinItem: (itemId: string) => void;
  leaveItem: (itemId: string) => void;
  setTyping: (itemId: string, isTyping: boolean) => void;
  markAsRead: (notificationIds: string[]) => void;
  clearNotification: (id: string) => void;
  clearAllNotifications: () => void;
}

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5000';

export const useWebSocket = (options: UseWebSocketOptions = {}): UseWebSocketReturn => {
  const {
    autoConnect = true,
    reconnection = true,
    reconnectionAttempts = 5,
    reconnectionDelay = 1000,
  } = options;

  const socketRef = useRef<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const connect = useCallback(() => {
    const token = localStorage.getItem('token');
    // WebSocket can authenticate via session cookie OR Bearer token
    // If no token in localStorage, the server will still check for the session cookie

    if (socketRef.current?.connected) {
      return;
    }

    setConnecting(true);
    setError(null);

    socketRef.current = io(SOCKET_URL, {
      auth: token ? { token } : {},
      withCredentials: true, // Send cookies for session-based auth
      reconnection,
      reconnectionAttempts,
      reconnectionDelay,
      transports: ['websocket', 'polling'],
    });

    socketRef.current.on('connect', () => {
      console.log('WebSocket connected');
      setConnected(true);
      setConnecting(false);
      setError(null);
    });

    socketRef.current.on('disconnect', (reason) => {
      console.log('WebSocket disconnected:', reason);
      setConnected(false);
    });

    socketRef.current.on('connect_error', (err) => {
      console.error('WebSocket connection error:', err);
      setError(err);
      setConnecting(false);
    });

    // Handle incoming notifications
    socketRef.current.on('notification', (notification: Notification) => {
      console.log('Received notification:', notification);
      setNotifications((prev) => [notification, ...prev]);
      setUnreadCount((prev) => prev + 1);
    });

    // Handle notifications marked as read
    socketRef.current.on('notifications-read', ({ notificationIds }) => {
      if (notificationIds === 'all') {
        setNotifications((prev) =>
          prev.map((n) => ({ ...n, isRead: true }))
        );
        setUnreadCount(0);
      } else {
        setNotifications((prev) =>
          prev.map((n) =>
            notificationIds.includes(n.id) ? { ...n, isRead: true } : n
          )
        );
        setUnreadCount((prev) => Math.max(0, prev - notificationIds.length));
      }
    });

    // Handle item updates (when viewing an item)
    socketRef.current.on('item-updated', (data) => {
      console.log('Item updated:', data);
      // This can be handled by specific components
    });

    // Handle new comments
    socketRef.current.on('new-comment', (data) => {
      console.log('New comment:', data);
    });

    // Handle typing indicators
    socketRef.current.on('user-typing', (data) => {
      console.log('User typing:', data);
    });
  }, [reconnection, reconnectionAttempts, reconnectionDelay]);

  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
      setConnected(false);
    }
  }, []);

  const joinItem = useCallback((itemId: string) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('join-item', itemId);
    }
  }, []);

  const leaveItem = useCallback((itemId: string) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('leave-item', itemId);
    }
  }, []);

  const setTyping = useCallback((itemId: string, isTyping: boolean) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('typing', { itemId, isTyping });
    }
  }, []);

  const markAsRead = useCallback((notificationIds: string[]) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('mark-read', notificationIds);
    }
    // Optimistically update local state
    if (notificationIds.length === 0) {
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } else {
      setNotifications((prev) =>
        prev.map((n) =>
          notificationIds.includes(n.id) ? { ...n, isRead: true } : n
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - notificationIds.length));
    }
  }, []);

  const clearNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    setUnreadCount((prev) => {
      const notification = notifications.find((n) => n.id === id);
      return notification && !notification.isRead ? prev - 1 : prev;
    });
  }, [notifications]);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
  }, []);

  // Auto-connect on mount
  useEffect(() => {
    if (autoConnect) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [autoConnect, connect, disconnect]);

  // Reconnect when token changes
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'token') {
        if (e.newValue) {
          connect();
        } else {
          disconnect();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [connect, disconnect]);

  return {
    socket: socketRef.current,
    connected,
    connecting,
    error,
    notifications,
    unreadCount,
    connect,
    disconnect,
    joinItem,
    leaveItem,
    setTyping,
    markAsRead,
    clearNotification,
    clearAllNotifications,
  };
};

export default useWebSocket;
